import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Brush,
} from "recharts";
import "./ComboChart.css";

const AvgIncomeChart = ({
  data,
  brushStartIndex,
  brushEndIndex,
  onBrushChange,
}) => {
  // Format data for Recharts - memoized to avoid recalculation
  const chartData = useMemo(() => {
    return data.map((item) => ({
      month: item.period_label || item.period,
      fullLabel: item.period_label,
      avgIncome: item.avg_income,
    }));
  }, [data]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="combo-chart-tooltip">
          <p className="tooltip-label">{data.fullLabel}</p>
          <p className="tooltip-avgIncome">
            <span className="tooltip-label-text">Avg$/Job</span>
            <span className="tooltip-value-text">
              ${data.avgIncome?.toFixed(2)}
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="combo-chart-container">
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            bottom: 20,
            left: 20,
          }}
        >
          <XAxis
            dataKey="month"
            axisLine={{ stroke: "#ADB5BD" }}
            tickLine={false}
            tick={{ fontSize: 14, fontWeight: 400, fill: "#6C757D" }}
          />
          <YAxis
            yAxisId="left"
            orientation="left"
            axisLine={{ stroke: "#ADB5BD" }}
            tickLine={false}
            tick={{ fontSize: 14, fontWeight: 400, fill: "#6C757D" }}
            tickFormatter={(value) => `$${value}`}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            axisLine={{ stroke: "#ADB5BD" }}
            tickLine={false}
            tick={{ fontSize: 14, fontWeight: 400, fill: "#6C757D" }}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ strokeDasharray: "3 3" }}
          />

          {/* Invisible Bar for consistent padding */}
          <Bar
            yAxisId="left"
            dataKey="avgIncome"
            fill="transparent"
            barSize={32}
          />

          <Line
            yAxisId="left"
            type="linear"
            dataKey="avgIncome"
            stroke="#DC3545"
            strokeWidth={2}
            dot={{ fill: "#FFFFFF", stroke: "#DC3545", strokeWidth: 2, r: 4 }}
            activeDot={{
              r: 6,
              stroke: "#DC3545",
              strokeWidth: 2,
              fill: "#FFFFFF",
            }}
          />
          <Brush
            dataKey="month"
            height={30}
            stroke="#0D6EFD"
            startIndex={brushStartIndex}
            endIndex={brushEndIndex}
            onChange={onBrushChange}
            alwaysShowText={true}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

AvgIncomeChart.propTypes = {
  data: PropTypes.array.isRequired,
  brushStartIndex: PropTypes.number.isRequired,
  brushEndIndex: PropTypes.number.isRequired,
  onBrushChange: PropTypes.func.isRequired,
};

export default AvgIncomeChart;
